/**
 * 🤝 Activate Partnership Modal
 * Convert a corporate account into a partnership
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Business, ContentCopy } from '@mui/icons-material';
import toast from 'react-hot-toast';
import {
  partnershipsService,
  PartnershipTypeDefinition,
  PartnershipType,
} from '@/services/partnershipsService';
import { CorporateAccount } from '@/services/corporateAccountsService';

interface ActivatePartnershipModalProps {
  open: boolean;
  account: CorporateAccount | null;
  onClose: () => void;
  onActivate: () => void;
}

export const ActivatePartnershipModal: React.FC<ActivatePartnershipModalProps> = ({
  open,
  account,
  onClose,
  onActivate,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [partnershipTypes, setPartnershipTypes] = useState<PartnershipTypeDefinition[]>([]);
  const [selectedType, setSelectedType] = useState<PartnershipType | ''>('');
  const [customRate, setCustomRate] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [successData, setSuccessData] = useState<{
    referralCode: string;
    partnershipType: string;
  } | null>(null);

  // Load partnership types when modal opens
  useEffect(() => {
    if (open) {
      loadPartnershipTypes();
      // Reset state
      setSelectedType('');
      setCustomRate('');
      setError(null);
      setSuccessData(null);
    }
  }, [open]);

  const loadPartnershipTypes = async () => {
    setLoadingTypes(true);
    try {
      const response = await partnershipsService.getPartnershipTypes();
      if (response.success && response.data) {
        const types = response.data.partnershipTypes || [];
        // Filter for active types only
        const activeTypes = types.filter((t) => t.isActive);
        setPartnershipTypes(activeTypes);
      } else {
        toast.error(response.error || 'Failed to load partnership types');
      }
    } catch (error) {
      console.error('Error loading partnership types:', error);
      toast.error('Failed to load partnership types');
    } finally {
      setLoadingTypes(false);
    }
  };

  const handleActivate = async () => {
    if (!account) return;
    if (!selectedType) {
      setError('Please select a partnership type');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const request = {
        corporateAccountId: account.id,
        partnershipType: selectedType,
        customCommissionRate: customRate ? parseFloat(customRate) : undefined,
      };

      const response = await partnershipsService.activatePartnership(request);

      if (response.success && response.data) {
        // Show success state
        setSuccessData({
          referralCode: response.data.referralCode,
          partnershipType: selectedType,
        });
        toast.success('Partnership activated successfully!');
        onActivate(); // Refresh parent list
      } else {
        setError(response.error || 'Failed to activate partnership');
        toast.error(response.error || 'Failed to activate partnership');
      }
    } catch (error: any) {
      console.error('Error activating partnership:', error);
      setError(error.message || 'An error occurred');
      toast.error('An error occurred while activating partnership');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyReferralCode = () => {
    if (successData?.referralCode) {
      navigator.clipboard.writeText(successData.referralCode);
      toast.success('Referral code copied to clipboard!');
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const getSelectedTypeDetails = () => {
    return partnershipTypes.find((t) => t.typeCode === selectedType);
  };

  const selectedTypeDetails = getSelectedTypeDetails();

  // Success View
  if (successData) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Business color="success" />
            <Typography variant="h6">Partnership Activated!</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              {account?.companyName} is now a partnership
            </Typography>
            <Typography variant="body2">
              The account has been converted to a{' '}
              {partnershipTypes.find((t) => t.typeCode === successData.partnershipType)?.displayName}{' '}
              partnership.
            </Typography>
          </Alert>

          <Box
            sx={{
              p: 3,
              bgcolor: 'background.default',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="overline" color="text.secondary">
              Referral Code
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              <Chip
                label={successData.referralCode}
                color="primary"
                sx={{
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  py: 2.5,
                  px: 1,
                }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopy />}
                onClick={handleCopyReferralCode}
              >
                Copy
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Share this code with the partner. They can use it to track referrals and earn commissions.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Check if account is already a partnership
  console.log('🤝 [ActivatePartnershipModal] Account:', {
    companyName: account?.companyName,
    isPartnership: account?.isPartnership,
    partnershipType: account?.partnershipType,
    referralCode: account?.referralCode,
  });

  if (account?.isPartnership) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Business color="warning" />
            <Typography variant="h6">Already a Partnership</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              {account.companyName} is already a partnership
            </Typography>
            <Typography variant="body2">
              This corporate account has already been converted to a partnership and cannot be activated again.
            </Typography>
          </Alert>

          <Box
            sx={{
              p: 3,
              bgcolor: 'background.default',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="overline" color="text.secondary">
              Existing Partnership Details
            </Typography>

            <Box mt={2} display="flex" flexDirection="column" gap={1}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Partnership Type
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {account.partnershipType?.replace('_', ' ').toUpperCase() || 'N/A'}
                </Typography>
              </Box>

              {account.referralCode && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Referral Code
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <Chip
                      label={account.referralCode}
                      color="primary"
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ContentCopy />}
                      onClick={() => {
                        if (account.referralCode) {
                          navigator.clipboard.writeText(account.referralCode);
                          toast.success('Referral code copied!');
                        }
                      }}
                    >
                      Copy
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            To manage this partnership, go to the Partnerships page or contact support to deactivate and reactivate with different settings.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Activation Form
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Business />
          <Typography variant="h6">Activate Partnership</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {account && (
          <Box mb={3}>
            <Typography variant="body2" color="text.secondary">
              Company
            </Typography>
            <Typography variant="h6">{account.companyName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {account.billingEmail || account.email}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box display="flex" flexDirection="column" gap={2.5}>
          <FormControl fullWidth disabled={loadingTypes || loading}>
            <InputLabel>Partnership Type *</InputLabel>
            <Select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as PartnershipType)}
              label="Partnership Type *"
            >
              {loadingTypes ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Loading types...
                </MenuItem>
              ) : partnershipTypes.length === 0 ? (
                <MenuItem disabled>No partnership types available</MenuItem>
              ) : (
                partnershipTypes.map((type) => (
                  <MenuItem key={type.id} value={type.typeCode}>
                    <Box>
                      <Typography variant="body1">{type.displayName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {type.commissionModel === 'flat' ? 'Flat' : 'Tiered'} Commission •{' '}
                        {type.baseCommissionRate}% base rate
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {selectedTypeDetails && (
            <Alert severity="info" sx={{ py: 1 }}>
              <Typography variant="caption" display="block">
                <strong>{selectedTypeDetails.displayName}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedTypeDetails.description}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Commission Model: <strong>{selectedTypeDetails.commissionModel}</strong> •
                Base Rate: <strong>{selectedTypeDetails.baseCommissionRate}%</strong>
              </Typography>
            </Alert>
          )}

          <TextField
            label="Custom Commission Rate (Optional)"
            type="number"
            value={customRate}
            onChange={(e) => setCustomRate(e.target.value)}
            disabled={loading}
            inputProps={{ min: 0, max: 100, step: 0.1 }}
            helperText={
              customRate
                ? `Override the base rate with ${customRate}%`
                : `Leave empty to use base rate (${selectedTypeDetails?.baseCommissionRate || 5}%)`
            }
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleActivate}
          variant="contained"
          disabled={loading || !selectedType}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Activating...' : 'Activate Partnership'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
